<template>
  <p
    v-if="teliaPayIsAnOption"
    class="max-w-546 mt-16 mb-16 experiment_tp-or-klarna_show-if-a"
  >
    Om du väljer att delbetala med Telia Finance kommer vi att skicka två
    fakturor. En faktura från Telia Finance som avser delbetalningen på din
    telefon och en faktura från Halebop som avser abonnemanget.
  </p>

  <p
    v-if="klarnaIsAnOption"
    class="max-w-546 mt-16 mb-16 experiment_tp-or-klarna_hide-if-a"
  >
    Om du väljer att delbetala med Klarna kommer vi att skicka två fakturor. En
    faktura från Klarna som avser delbetalningen på din telefon och en faktura
    från Halebop som avser abonnemanget.
  </p>

  <InputRadioGroup>
    <template
      v-for="method in paymentMethods"
      :key="method.commaSeparatedSystemNameAndIdentifier"
    >
      <div
        v-if="method.commaSeparatedSystemNameAndIdentifier === CASH_ON_DELIVERY"
      >
        <InputRadioWithContent
          v-model="selectedPaymentMethodNameAndIdentifier"
          :value="method.commaSeparatedSystemNameAndIdentifier"
          :label="
            checkoutStore.fetchedData.cart.cartItems.find(
              (item) => item.Category === Category.prePaid,
            )
              ? method.name
              : 'Betala med faktura'
          "
          :group-name="radioButtonsGroupName"
          :root-extra-classes="RADIO_BUTTONS_EXTRA_CLASSES"
        >
          <template #visible>
            <p class="max-w-546 mt-6">
              {{ method.description }}
            </p>
          </template>
        </InputRadioWithContent>
      </div>

      <div
        v-else-if="
          method.commaSeparatedSystemNameAndIdentifier === SWISH_METHOD
        "
      >
        <InputRadioWithContent
          v-model="selectedPaymentMethodNameAndIdentifier"
          :value="method.commaSeparatedSystemNameAndIdentifier"
          label="Swish"
          :secondary-label="`${checkoutStore.payment_directPaymentSumToDisplay.toLocaleString('sv-SE')} kr`"
          :group-name="radioButtonsGroupName"
          :root-extra-classes="RADIO_BUTTONS_EXTRA_CLASSES"
        >
          <template #hidden>
            <InputText
              v-model="checkoutStore.userState.payment.inputtedSwishPhoneNumber"
              type="text"
              :name="SWISH_NUMBER"
              :input-id="SWISH_NUMBER"
              label="Swish Telefonnummer"
              placeholder="0701234567"
              help-text="Rätt format: 0701234567"
              mode="eager"
            />
          </template>
          <template #img>
            <NuxtImg
              src="/images/payment_logos/swish_light.svg"
              alt="Logga för Swish"
              loading="lazy"
              class="block dark:hidden h-28"
              provider="cloudfront"
            />
            <NuxtImg
              src="/images/payment_logos/swish_dark.svg"
              alt="Logga för Swish"
              loading="lazy"
              class="hidden dark:block h-28"
              provider="cloudfront"
            />
          </template>
        </InputRadioWithContent>
      </div>

      <div
        v-else-if="
          method.commaSeparatedSystemNameAndIdentifier === TELIAPAY_METHOD
        "
        class="experiment_tp-or-klarna_show-if-a"
      >
        <InputRadioWithContent
          v-model="selectedPaymentMethodNameAndIdentifier"
          :value="method.commaSeparatedSystemNameAndIdentifier"
          label="Delbetala telefonen räntefritt"
          :secondary-label="
            checkoutStore.payment_partialPaymentSumToDisplay
              ? `${checkoutStore.payment_partialPaymentSumToDisplay.toLocaleString('sv-SE')} kr/mån`
              : ''
          "
          :group-name="radioButtonsGroupName"
          :root-extra-classes="RADIO_BUTTONS_EXTRA_CLASSES"
        >
          <template #hidden>
            <div
              :id="TELIA_PAY_LIVE_REGION"
              aria-live="polite"
              aria-atomic
              class="sr-only"
            >
              <p v-if="!checkoutStore.ui.loading.isStep4TeliaPayInitiated">
                Läser in Telia Finance för delbetalning
              </p>
              <p v-if="checkoutStore.ui.loading.isStep4TeliaPayInitiated">
                Telia Finance för delbetalning har lästs in
              </p>
            </div>

            <template v-if="checkoutStore.teliaPayToken">
              <CheckoutPaymentTeliaPayOption
                @on-reset-payment-option="resetSelectedPaymentMethod"
              />
            </template>
            <CheckoutPaymentTeliaPayLoadingUI
              :show="!checkoutStore.ui.loading.isStep4TeliaPayInitiated"
            />
          </template>
          <template #img>
            <NuxtImg
              src="/shop/images/checkout/teliafinance-rect.svg"
              alt="Logga för Telia Finance"
              loading="lazy"
              class="h-28"
              provider="cloudfront"
            />
          </template>
        </InputRadioWithContent>
      </div>

      <div
        v-else-if="
          method.commaSeparatedSystemNameAndIdentifier === CARD_PAYMENT_METHOD
        "
      >
        <InputRadioWithContent
          v-model="selectedPaymentMethodNameAndIdentifier"
          :value="method.commaSeparatedSystemNameAndIdentifier"
          label="Betalkort"
          :secondary-label="`${checkoutStore.payment_directPaymentSumToDisplay.toLocaleString('sv-SE')} kr`"
          :group-name="radioButtonsGroupName"
          :root-extra-classes="RADIO_BUTTONS_EXTRA_CLASSES"
        >
          <template #img>
            <div class="flex dark:hidden flex-row items-center">
              <NuxtImg
                src="/images/payment_logos/visa_light.svg"
                alt="Logga för Visa"
                loading="lazy"
                class="h-20 me-4"
                provider="cloudfront"
              />
              <NuxtImg
                src="/images/payment_logos/mastercard.svg"
                alt="Logga för Mastercard"
                loading="lazy"
                class="h-20"
                provider="cloudfront"
              />
            </div>
            <div class="hidden dark:flex flex-row items-center">
              <NuxtImg
                src="/images/payment_logos/visa_dark.svg"
                alt="Logga för Visa"
                loading="lazy"
                class="h-20 me-4"
                provider="cloudfront"
              />
              <NuxtImg
                src="/images/payment_logos/mastercard.svg"
                alt="Logga för Mastercard"
                loading="lazy"
                class="h-20"
                provider="cloudfront"
              />
            </div>
          </template>
        </InputRadioWithContent>
      </div>

      <div
        v-else-if="
          method.commaSeparatedSystemNameAndIdentifier ===
          KLARNA_PAY_NOW_IDENTIFIER
        "
        class="experiment_tp-or-klarna_hide-if-a"
      >
        <InputRadioWithContent
          v-model="selectedPaymentMethodNameAndIdentifier"
          :value="method.commaSeparatedSystemNameAndIdentifier"
          label="Betala direkt"
          :secondary-label="`${checkoutStore.payment_directPaymentSumToDisplay.toLocaleString('sv-SE')} kr`"
          :group-name="radioButtonsGroupName"
          :root-extra-classes="RADIO_BUTTONS_EXTRA_CLASSES"
          hidden-field-will-still-render
        >
          <template #hidden>
            <p class="mt-4 ms-40">Kort, Swish och bank</p>
            <CheckoutPaymentKlarnaOption
              class="mt-4 ms-40"
              klarna-rood-div-id="checkout__klarna-container-el_direct"
              :klarna-category-identifier="
                method.klarnaPaymentMethodCategoryIdentifier
              "
            />
          </template>
          <template #img>
            <NuxtImg
              src="/images/payment_logos/klarna.svg"
              alt="Logga för Klarna"
              loading="lazy"
              class="h-19 md:h-24"
              provider="cloudfront"
            />
          </template>
        </InputRadioWithContent>
      </div>

      <div
        v-else-if="
          method.commaSeparatedSystemNameAndIdentifier ===
          KLARNA_PAY_LATER_IDENTIFIER
        "
        class="experiment_tp-or-klarna_hide-if-a"
      >
        <InputRadioWithContent
          v-model="selectedPaymentMethodNameAndIdentifier"
          :value="method.commaSeparatedSystemNameAndIdentifier"
          label="Faktura 30 dagar"
          :secondary-label="`${checkoutStore.payment_directPaymentSumToDisplay.toLocaleString('sv-SE')} kr`"
          :group-name="radioButtonsGroupName"
          hidden-field-will-still-render
          :root-extra-classes="RADIO_BUTTONS_EXTRA_CLASSES"
        >
          <template #hidden>
            <CheckoutPaymentKlarnaOption
              class="mt-4 ms-40"
              klarna-rood-div-id="checkout__klarna-container-el_invoice"
              :klarna-category-identifier="
                method.klarnaPaymentMethodCategoryIdentifier
              "
            />
          </template>
          <template #img>
            <NuxtImg
              src="/images/payment_logos/klarna.svg"
              alt="Logga för Klarna"
              loading="lazy"
              class="h-19 md:h-24"
              provider="cloudfront"
            />
          </template>
        </InputRadioWithContent>
      </div>

      <div
        v-else-if="
          method.commaSeparatedSystemNameAndIdentifier ===
          KLARNA_PARTPAYMENT_IDENTIFIER
        "
        class="experiment_tp-or-klarna_hide-if-a"
      >
        <InputRadioWithContent
          v-model="selectedPaymentMethodNameAndIdentifier"
          :value="method.commaSeparatedSystemNameAndIdentifier"
          label="Delbetala telefonen räntefritt"
          :secondary-label="
            checkoutStore.payment_partialPaymentSumToDisplay
              ? `${checkoutStore.payment_partialPaymentSumToDisplay.toLocaleString('sv-SE')} kr/mån i ${checkoutStore.userState.payment.initialDownpayTime} månader`
              : ''
          "
          :group-name="radioButtonsGroupName"
          hidden-field-will-still-render
          :root-extra-classes="RADIO_BUTTONS_EXTRA_CLASSES"
        >
          <template #hidden>
            <p class="mt-4 ms-40">Dela upp i 12-36 månader</p>
            <CheckoutPaymentKlarnaOption
              class="mt-4 ms-40"
              klarna-rood-div-id="checkout__klarna-container-el_partpayment"
              :klarna-category-identifier="
                method.klarnaPaymentMethodCategoryIdentifier
              "
            />
            <AppLoanWarningBox class="mt-16" />
          </template>
          <template #img>
            <NuxtImg
              src="/images/payment_logos/klarna.svg"
              alt="Logga för Klarna"
              loading="lazy"
              class="h-19 md:h-24"
              provider="cloudfront"
            />
          </template>
        </InputRadioWithContent>
      </div>

      <div v-else>
        <InputRadioWithContent
          v-model="selectedPaymentMethodNameAndIdentifier"
          :value="method.commaSeparatedSystemNameAndIdentifier"
          :label="method.name"
          :group-name="radioButtonsGroupName"
          :root-extra-classes="RADIO_BUTTONS_EXTRA_CLASSES"
        />
      </div>
    </template>
  </InputRadioGroup>
  <div class="w-full experiment_tp-or-klarna_hide-if-a">
    <CheckoutPaymentKlarnaRadioRadioOptionsLoadingUI
      upper-row-text-width-size="medium"
      :show="checkoutStore.ui.loading.isStep4KlarnaMethodsLoading"
    />
  </div>
  <div class="w-full experiment_tp-or-klarna_hide-if-a">
    <CheckoutPaymentKlarnaRadioRadioOptionsLoadingUI
      upper-row-text-width-size="short"
      class="experiment_tp-or-klarna_hide-if-a"
      :show="checkoutStore.ui.loading.isStep4KlarnaMethodsLoading"
    />
  </div>
  <div class="w-full experiment_tp-or-klarna_hide-if-a">
    <CheckoutPaymentKlarnaRadioRadioOptionsLoadingUI
      upper-row-text-width-size="long"
      class="experiment_tp-or-klarna_hide-if-a"
      :show="checkoutStore.ui.loading.isStep4KlarnaMethodsLoading"
    />
  </div>
</template>

<script lang="ts" setup>
import { SWISH_NUMBER } from '~/models/checkout/forms/Step4Form.model';
import {
  TELIAPAY_METHOD,
  CARD_PAYMENT_METHOD,
  SWISH_METHOD,
  CASH_ON_DELIVERY,
  PAYMENT_METHODS_THAT_ARE_MONTHLY_PAYMENT,
  KLARNA_PAY_NOW_IDENTIFIER,
  KLARNA_PAY_LATER_IDENTIFIER,
  KLARNA_PARTPAYMENT_IDENTIFIER,
  type PaymentMethod,
} from '~/models/checkout/PaymentMethods.model';
import { Category } from '~/models/Category.models';
const checkoutStore = useMyCheckoutStore();
const { fetchAndInitTeliaPay } = useTeliaPayFetchScript();
const TELIA_PAY_LIVE_REGION = 'telia-pay-live-region';
const { ui } = storeToRefs(checkoutStore);

const props = withDefaults(
  defineProps<{
    radioButtonsGroupName: string;
    paymentMethods?: PaymentMethod[];
  }>(),
  {
    paymentMethods: () => [],
  },
);

const teliaPayIsAnOption = computed(
  () =>
    !!props.paymentMethods.find(
      (method: PaymentMethod) => method.systemName === TELIAPAY_METHOD,
    ),
);

const klarnaIsAnOption = computed(
  () =>
    !!props.paymentMethods.find((method: PaymentMethod) =>
      method.commaSeparatedSystemNameAndIdentifier
        .toLocaleLowerCase()
        .includes('klarna'),
    ),
);

const klarnaDeniedUserOrAvailabilityWatcher = computed(
  () => checkoutStore.ui.klarnaDeniedUserOrAvailability,
);

const selectedPaymentMethodNameAndIdentifier = ref<string>('');
const RADIO_BUTTONS_EXTRA_CLASSES = 'py-4';

const pushMessageToTeliaPayLiveRegion = (msg: string) => {
  const liveRegionElement = document.getElementById(TELIA_PAY_LIVE_REGION);
  if (liveRegionElement) {
    const p = document.createElement('p');
    p.textContent = msg;
    liveRegionElement.appendChild(p);
  }
};

const resetPaymentSelectedStateOnly = () => {
  checkoutStore.userState.payment.selectedPaymentMethodSystemName = undefined;
  checkoutStore.userState.payment.selectedPaymentMethodCommaSeparatedIdentifier =
    undefined;
  checkoutStore.userState.payment.selectedPaymentMethodCleanName = undefined;
  selectedPaymentMethodNameAndIdentifier.value = '';
};

const resetSelectedPaymentMethod = async () => {
  resetPaymentSelectedStateOnly();
  await fetchAndInitTeliaPay();
};

onMounted(() => {
  if (import.meta.client) {
    resetPaymentSelectedStateOnly();
  }
});

watch(
  () => props.paymentMethods,
  (methods) => {
    if (methods.length === 1) {
      selectedPaymentMethodNameAndIdentifier.value =
        methods[0].commaSeparatedSystemNameAndIdentifier;
    }
  },
);

watch(
  selectedPaymentMethodNameAndIdentifier,
  (
    methodNameAndIdentifier?: string,
    previousMethodNameAndIdentifier?: string,
  ) => {
    if (!methodNameAndIdentifier) {
      return;
    }

    if (methodNameAndIdentifier !== previousMethodNameAndIdentifier) {
      checkoutStore.userState.payment.inputtedSwishPhoneNumber = '';
    }

    const paymentMethodThatWasSelected = props.paymentMethods.find(
      (method) =>
        method.commaSeparatedSystemNameAndIdentifier ===
        methodNameAndIdentifier,
    );

    if (!paymentMethodThatWasSelected) {
      return;
    }

    if (
      PAYMENT_METHODS_THAT_ARE_MONTHLY_PAYMENT.includes(
        paymentMethodThatWasSelected.commaSeparatedSystemNameAndIdentifier,
      )
    ) {
      checkoutStore.userState.payment.selectedDownPayTime =
        checkoutStore.userState.payment.initialDownpayTime;
    } else {
      checkoutStore.userState.payment.selectedDownPayTime = 1;
    }

    checkoutStore.userState.payment.selectedPaymentMethodSystemName =
      paymentMethodThatWasSelected.systemName;

    checkoutStore.userState.payment.selectedPaymentMethodCommaSeparatedIdentifier =
      paymentMethodThatWasSelected.commaSeparatedSystemNameAndIdentifier;

    checkoutStore.userState.payment.selectedPaymentMethodCleanName =
      paymentMethodThatWasSelected.name;

    const { pushAddPaymentInfoEvent } = useGoogleAnalyticsEvents();
    pushAddPaymentInfoEvent(paymentMethodThatWasSelected.name);
  },
  { immediate: true },
);

watch(selectedPaymentMethodNameAndIdentifier, async (methodName) => {
  if (methodName === TELIAPAY_METHOD) {
    setTimeout(() => {
      pushMessageToTeliaPayLiveRegion(
        'Läser in Telia Finance för delbetalning',
      );
    }, 200);
  }
});

watch(ui, (uiValue, uiPrevValue) => {
  if (
    uiPrevValue.loading.isStep4TeliaPayInitiated === false &&
    uiValue.loading.isStep4TeliaPayInitiated === true
  ) {
    pushMessageToTeliaPayLiveRegion(
      'Telia Finance för delbetalning har lästs in',
    );
  }
});

watch(klarnaDeniedUserOrAvailabilityWatcher, (value, prevValue) => {
  // Klarna has descided it's not available as a payment method, reset selected method
  if (value === true && prevValue === false) {
    resetSelectedPaymentMethod();
  }
});
</script>

<style lang="scss" scoped></style>
